import { ArrowUpRight, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { PrecisionNote } from "@/components/ui/precision-note";
import { Sheet } from "@/components/ui/sheet";
import { cn } from "@/lib/utils/cn";

import { useFinancialAidDetailQuery } from "../../api/hooks";
import { formatAmounts, formatEligibility } from "../../format";
import type { FinancialAidCard } from "../../types";
import { ErrorState, Loading } from "../ui/states";

/**
 * Detail sheet for one financial aid (OAC-441, étape 4). Opened from a directory
 * card; fetches the full record (program number, contact, territories, official
 * link) scoped to the action. The heart mirrors the list favourite state.
 */
export function DetailSheet({
  action,
  aidId,
  isFavorite,
  isToggling = false,
  onToggleFavorite,
  onClose,
}: {
  action: string;
  aidId: string;
  isFavorite: boolean;
  isToggling?: boolean;
  onToggleFavorite: (aid: FinancialAidCard) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detail = useFinancialAidDetailQuery(action, aidId);
  const contactName = [
    detail.data?.contact_first_name,
    detail.data?.contact_last_name,
  ]
    .filter(Boolean)
    .join(" ");
  // The free-text "Autre" précisions are prose, not catalogue values: they read as
  // a labelled line under the field, exactly like on the Django sheet.
  const regions = (detail.data?.service_regions ?? []).filter(Boolean);
  const sectors = (detail.data?.sectors_served ?? []).filter(Boolean);
  const regionsOther = detail.data?.service_regions_other;
  const sectorsOther = detail.data?.sectors_served_other;

  return (
    <Sheet
      title={t("financialAids.detail.title")}
      side="right"
      className="w-[32rem]"
      onClose={onClose}
    >
      <div className="flex-1 overflow-y-auto p-5">
        {detail.isPending && <Loading />}
        {detail.isError && <ErrorState onRetry={() => void detail.refetch()} />}
        {detail.data && (
          <article className="space-y-5">
            <header className="space-y-2">
              <h2 className="text-xl font-bold leading-tight">
                {detail.data.title}
              </h2>
              {detail.data.program_number && (
                <p className="text-sm text-muted-foreground">
                  {t("financialAids.detail.programNumber", {
                    number: detail.data.program_number,
                  })}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {detail.data.funding_types_display.map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </header>

            {detail.data.summary && (
              <p className="whitespace-pre-line text-sm text-muted-foreground">
                {detail.data.summary}
              </p>
            )}

            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t("financialAids.detail.organization")}>
                {detail.data.organization}
              </Field>
              <Field label={t("financialAids.detail.fundingTypes")}>
                {detail.data.funding_types_display.join(", ")}
              </Field>
              <Field label={t("financialAids.detail.amounts")}>
                {formatAmounts(detail.data, t)}
              </Field>
              <Field label={t("financialAids.detail.eligibility")}>
                {formatEligibility(detail.data, t)}
              </Field>
              {(regions.length > 0 || regionsOther) && (
                <Field
                  label={t("financialAids.detail.regions")}
                  precision={regionsOther}
                >
                  {regions.join(", ")}
                </Field>
              )}
              {detail.data.territories.length > 0 && (
                <Field label={t("financialAids.detail.territories")}>
                  {detail.data.territories.join(", ")}
                </Field>
              )}
              {(sectors.length > 0 || sectorsOther) && (
                <Field
                  label={t("financialAids.detail.sectors")}
                  precision={sectorsOther}
                >
                  {sectors.join(", ")}
                </Field>
              )}
              {/* Contact person — the aid's équivalent of an expert respondent. */}
              {contactName && (
                <Field label={t("financialAids.detail.contactPerson")}>
                  {contactName}
                  {detail.data.contact_function && (
                    <span className="text-muted-foreground">
                      {" · "}
                      {detail.data.contact_function}
                    </span>
                  )}
                </Field>
              )}
              {detail.data.contact_email && (
                <Field label={t("financialAids.detail.contactEmail")}>
                  <a
                    className="underline underline-offset-4"
                    href={`mailto:${detail.data.contact_email}`}
                  >
                    {detail.data.contact_email}
                  </a>
                </Field>
              )}
              {detail.data.contact_phone && (
                <Field label={t("financialAids.detail.contactPhone")}>
                  <a
                    className="underline underline-offset-4"
                    href={`tel:${detail.data.contact_phone}`}
                  >
                    {detail.data.contact_phone}
                  </a>
                  {detail.data.contact_extension && (
                    <span className="text-muted-foreground">
                      {" "}
                      {t("financialAids.detail.extension", {
                        ext: detail.data.contact_extension,
                      })}
                    </span>
                  )}
                </Field>
              )}
            </dl>

            <div className="flex items-center gap-2 border-t border-border pt-4">
              {detail.data.official_url && (
                <Button asChild>
                  <a
                    href={detail.data.official_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("financialAids.detail.consult")}
                    <ArrowUpRight className="ml-1 size-4" aria-hidden />
                  </a>
                </Button>
              )}
              <Button
                variant="outline"
                aria-pressed={isFavorite}
                disabled={isToggling}
                onClick={() => onToggleFavorite(detail.data)}
                className="gap-2"
              >
                <Heart
                  className={cn(
                    "size-4",
                    isFavorite
                      ? "fill-destructive text-destructive"
                      : "text-muted-foreground",
                  )}
                  aria-hidden
                />
                {isFavorite
                  ? t("financialAids.card.unfavorite")
                  : t("financialAids.card.favorite")}
              </Button>
            </div>
          </article>
        )}
      </div>
    </Sheet>
  );
}

function Field({
  label,
  wide = false,
  precision,
  children,
}: {
  label: string;
  wide?: boolean;
  /** Free-text "Autre" précision, rendered as a labelled line under the value. */
  precision?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(wide && "sm:col-span-2")}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">
        {children}
        <PrecisionNote text={precision} className="mt-1" />
      </dd>
    </div>
  );
}
